import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import type { CogniPrintProfile } from '@shared/types/cogniprint';
import { getDimensionsFromProfile } from '../dashboard/dimensionHelpers';
import { DimensionMeter } from './DimensionMeter';

interface CogniPrintChartProps {
  profile: CogniPrintProfile;
  variant: 'reveal' | 'compact' | 'teaser';
  animated: boolean;
  /** Free-tier viewer: paid dimensions render locked rather than "Not assessed". */
  isFree: boolean;
  changedDimensions?: string[];
}

/**
 * Every diagnostic dimension, drawn by the shared model and the shared meter.
 *
 * This used to compute its own bar values and used to hide the paid dimensions
 * entirely unless told otherwise — which is why a learner who had just completed
 * the FULL diagnostic saw four bars on the reveal page and seven on the report
 * beside the chat. Now it always shows all seven; the free tier sees locks.
 */
export function CogniPrintChart({ profile, variant, animated, isFree, changedDimensions }: CogniPrintChartProps) {
  const changedSet = new Set(changedDimensions ?? []);
  const isCompact = variant !== 'reveal';
  const dimensions = getDimensionsFromProfile(profile, isFree).filter((d) => d.key !== 'priorKnowledge');

  return (
    <div className={`w-full ${isCompact ? 'space-y-2' : 'space-y-4'}`}>
      {dimensions.map((dim, i) => {
        const isChanged = changedSet.has(dim.key);
        const isClear = dim.clarity === 'clear';
        return (
          <motion.div
            key={dim.key}
            initial={animated ? { opacity: 0, x: -20 } : undefined}
            animate={{ opacity: 1, x: 0 }}
            transition={animated ? { delay: i * 0.08, duration: 0.3 } : undefined}
          >
            <div className="flex items-center justify-between mb-1">
              <span className={`${isCompact ? 'text-xs' : 'text-sm'} font-body ${dim.locked ? 'text-content-dim' : 'text-content-secondary'}`}>
                {dim.name}
              </span>
              {dim.locked ? (
                <span className="flex items-center gap-1 text-xs text-content-dim">
                  <Lock size={10} />
                  Upgrade to unlock
                </span>
              ) : (
                <span className={`text-xs font-body font-semibold ${!dim.assessed ? 'text-content-dim italic' : isClear ? 'text-gold-500' : 'text-content-muted'}`}>
                  {dim.label}
                  {isChanged && (
                    <motion.span
                      className="ml-1.5 text-[10px] text-gold-400 font-normal"
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 0 }}
                      transition={{ delay: 5, duration: 0.5 }}
                    >
                      Updated
                    </motion.span>
                  )}
                </span>
              )}
            </div>
            <DimensionMeter dim={dim} size={isCompact ? 'compact' : 'full'} animate={animated} delay={i * 80 + 150} />
          </motion.div>
        );
      })}
    </div>
  );
}
